import { ReferenceMonth } from 'bold-ui'
import {
  RastreioCancerPerguntaDbEnum,
  RastreioCancerPerguntaOpcaoEnum,
  UltimoExameCitopatologicoQuery,
} from 'graphql/types.generated'
import { afterBirthdate, beforeEqualToday, createValidator, minLength, required } from 'util/validation'

import { CitopatologicoPerguntaField, RequisicaoExameCitopatologicoFormValues } from './model-citopatologico'

const MSG_POSTERIOR_NASCIMENTO = 'Deve ser posterior à data de nascimento.'
const MSG_ANTERIOR_HOJE = 'Deve ser anterior ou igual à data atual.'

function referenceMonthAfterBirthDate(refMonth: ReferenceMonth, dataNascimento: string): string | undefined {
  const birth = new Date(dataNascimento)
  const birthYear = birth.getFullYear()
  const birthMonth = birth.getMonth()
  if (refMonth.year < birthYear || (refMonth.year === birthYear && refMonth.month < birthMonth)) {
    return MSG_POSTERIOR_NASCIMENTO
  }
  return undefined
}

function referenceMonthBeforeOrEqualToday(refMonth: ReferenceMonth): string | undefined {
  const today = new Date()
  const todayYear = today.getFullYear()
  const todayMonth = today.getMonth()
  if (refMonth.year > todayYear || (refMonth.year === todayYear && refMonth.month > todayMonth)) {
    return MSG_ANTERIOR_HOJE
  }
  return undefined
}

export const citopatologicoAnamneseValidator = (
  dataNascimento: string,
  ultimoExame?: UltimoExameCitopatologicoQuery['ultimaSolicitacaoCitopatologicoUtero']
) =>
  createValidator<RequisicaoExameCitopatologicoFormValues>(
    {
      PERGUNTA_CITO_01_01: [required],
      PERGUNTA_CITO_01_03: [required],
      PERGUNTA_CITO_01_07: [required],
      PERGUNTA_CITO_01_08: [required],
      PERGUNTA_CITO_01_11: [required],
      PERGUNTA_CITO_01_12: [required],
    },
    (values, errors) => {
      if (!values) return errors

      const examePreventivo = values[CitopatologicoPerguntaField.examePreventivo]
      const dataPreventivo = (values[CitopatologicoPerguntaField.dataPreventivo] as unknown) as
        | ReferenceMonth
        | null
        | undefined
      const dum = values[CitopatologicoPerguntaField.dum] as string

      if (examePreventivo === RastreioCancerPerguntaOpcaoEnum.SIM_PAPANICOLAU) {
        errors[CitopatologicoPerguntaField.dataPreventivo] = required(dataPreventivo)
      }

      if (dataPreventivo) {
        errors[CitopatologicoPerguntaField.dataPreventivo] =
          referenceMonthAfterBirthDate(dataPreventivo, dataNascimento) ??
          referenceMonthBeforeOrEqualToday(dataPreventivo)
      }

      if (!values.checkDum) {
        errors[CitopatologicoPerguntaField.dum] = required(values[CitopatologicoPerguntaField.dum])
      }

      if (values[CitopatologicoPerguntaField.dum]) {
        errors[CitopatologicoPerguntaField.dum] = afterBirthdate(dum, dataNascimento) ?? beforeEqualToday(dum)
      }

      const motivoExame = values[CitopatologicoPerguntaField.motivoExame]
      if (motivoExame === RastreioCancerPerguntaOpcaoEnum.RASTREAMENTO && ultimoExame) {
        const respostas = ultimoExame.respostas
        const motivoUltimo = respostas?.find((r) => r?.pergunta === RastreioCancerPerguntaDbEnum.PERGUNTA_CITO_01_01)
          ?.opcao
        const situacaoEspecialUltimo = respostas?.find(
          (r) => r?.pergunta === RastreioCancerPerguntaDbEnum.PERGUNTA_CITO_01_02
        )?.opcao
        const dataColetaUltimo = respostas?.find(
          (r) => r?.pergunta === RastreioCancerPerguntaDbEnum.PERGUNTA_CITO_03_01
        )?.data

        const imunossupressao =
          situacaoEspecialUltimo === RastreioCancerPerguntaOpcaoEnum.PESSOA_VIVENDO_COM_HIV ||
          situacaoEspecialUltimo === RastreioCancerPerguntaOpcaoEnum.OUTRAS_SITUACOES_IMUNOSSUPRESSAO

        if (
          motivoUltimo === RastreioCancerPerguntaOpcaoEnum.RASTREAMENTO &&
          imunossupressao &&
          dataColetaUltimo != null
        ) {
          const tresAnosAtras = new Date()
          tresAnosAtras.setFullYear(tresAnosAtras.getFullYear() - 3)
          if (new Date(dataColetaUltimo) > tresAnosAtras) {
            errors[CitopatologicoPerguntaField.motivoExame] = 'Última coleta realizada há menos de 3 anos.'
          }
        }
      }

      return errors
    }
  )

export const citopatologicoExameClinicoValidator = createValidator<RequisicaoExameCitopatologicoFormValues>({
  PERGUNTA_CITO_02_01: [required],
  PERGUNTA_CITO_02_02: [required],
})

export const citopatologicoDadosDaColetaValidator = (dataNascimento: string) =>
  createValidator<RequisicaoExameCitopatologicoFormValues>(
    {
      PERGUNTA_CITO_03_01: [required],
      PERGUNTA_CITO_03_02: [required],
      PERGUNTA_CITO_03_03: [minLength(15), required],
      PERGUNTA_CITO_03_04: [required],
      PERGUNTA_CITO_03_05: [required],
    },
    (values, errors) => {
      if (!values) return errors

      const dataDaColeta = values[CitopatologicoPerguntaField.dataDaColeta] as string

      if (dataDaColeta) {
        errors[CitopatologicoPerguntaField.dataDaColeta] =
          afterBirthdate(dataDaColeta, dataNascimento) ?? beforeEqualToday(dataDaColeta)
      }

      return errors
    }
  )
